import { Link } from 'react-router-dom';
import { ArrowRightIcon, GiftIcon, RepeatIcon } from 'lucide-react';
import { RoutineCard } from '../shared/RoutineCard';
import { HeadingText } from '../shared/HeadingText';
import { GiftTierList } from '../routines/GiftTierList';
import { useCatalog } from '../../contexts/CatalogContext';
import { useFeatures } from '../../hooks/useFeatures';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { featuredRoutines } from './homeRules';

/**
 * "Het complete plan, kant-en-klaar": de uitgelichte protocollen naast de cadeaustaffel, en het
 * abonnement als één regel eronder. Zonder protocollen geen blok.
 */
export function HomeProtocols() {
  const pages = usePages();
  const {
    routines
  } = useCatalog();
  const {
    subscriptions
  } = useFeatures();
  const featured = featuredRoutines(routines);
  const copy = pages.home.protocols;
  if (featured.length === 0) return null;
  return <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="home-protocols" data-id="element-7429" data-mp="3l7o6f5bh6l4e">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7430" data-mp="3q7k3eklr9cag">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7431" data-mp="3q4o2qoiiw9ib">
          <div className="lg:col-span-4" data-id="element-7432" data-mp="3c8klrwwntxns">
            <h2 id="home-protocols" className={`${sectionHeading} text-ink`} data-id="element-7433" data-mp="3g8usxcw6uu48">
              <HeadingText heading={copy.heading} data-id="element-7434" data-mp="3gaxglq9vcb5s" />
            </h2>
            {copy.body && <p className="mt-6 text-[17px] leading-relaxed text-muted" data-id="element-7435" data-mp="7tktydnli50o">{fill(copy.body)}</p>}

            <GiftTierList className="mt-8" data-id="element-7436" data-mp="cqt6nypz5guj" />
            <p className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted" data-id="element-7437" data-mp="3v63n0ecvmbzn">
              <GiftIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7438" data-mp="32b8lp92xnul" />
              {fill(copy.note)}
            </p>

            {copy.cta && <Link to="/routines" className={`mt-6 ${quietLink} text-ink`} data-id="element-7439" data-mp="qpsvb6dhlvqp">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7440" data-mp="15em302co7u36" />
              </Link>}
          </div>

          <ul className="grid gap-6 md:grid-cols-2 lg:col-span-8" data-id="element-7441" data-mp="11e268zw0eshk">
            {featured.map(r => <li key={r.id} className="h-full bg-sand p-7 lg:p-9" data-id="element-7442" data-mp="wcuvqt2hff77">
                <RoutineCard routine={r} data-id="element-7443" data-mp="rf3502dkpz4l" />
              </li>)}
          </ul>
        </div>

        {/* Het abonnement als één regel: de uitleg zelf staat bij Protocollen en in de webshop. */}
        {subscriptions && <Link to="/routines" className="group mt-14 flex flex-col gap-2 border-t border-line pt-7 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-id="element-7444" data-mp="ho8906aizl5y">
            <span className="flex items-start gap-3 text-[16px] leading-relaxed text-ink" data-id="element-7445" data-mp="8olgyb3orrpk">
              <RepeatIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7446" data-mp="3p8u3qctekmz" />
              <span data-id="element-7447" data-mp="3uvtkr8yyjqjh">
                <span className="font-display text-xl" data-id="element-7448" data-mp="3uva21q1hgy4e">{pages.subscription.name}</span>
                <span className="text-muted" data-id="element-7449" data-mp="3ks4p6u9mkkqn"> · {pages.subscription.claim}</span>
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-2 pl-7 text-[15px] text-ink group-hover:underline group-hover:underline-offset-[6px] sm:pl-0" data-id="element-7450" data-mp="3ppofisjlknyy">
              Zo werkt het
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7451" data-mp="3jrdjoy7nmti" />
            </span>
          </Link>}
      </div>
    </section>;
}